import { useStatusCheck } from '@sunstone/api-client/hooks';
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  cn,
} from '@sunstone/ui';
import { Database, RefreshCw, Server } from 'lucide-react';

function LatencyBar({
  label,
  icon: Icon,
  latencyUs,
  status,
}: {
  label: string;
  icon: React.ElementType;
  latencyUs: number;
  status: string;
}) {
  const ms = latencyUs / 1000;
  const isHealthy = status.toLowerCase() === 'ok' || status.toLowerCase() === 'healthy';

  return (
    <div className="flex items-center gap-4">
      <div className="flex items-center gap-2 w-24 flex-shrink-0">
        <Icon className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-sm text-muted-foreground">{label}</span>
      </div>
      <div className="flex items-center gap-3 flex-1">
        <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
          <div
            className={cn(
              'h-full rounded-full transition-all duration-700',
              isHealthy ? 'bg-[#00D4A4]' : 'bg-amber-500',
            )}
            style={{ width: `${Math.min(100, (ms / 50) * 100)}%` }}
          />
        </div>
        <span
          className={cn(
            'font-mono text-xs tabular-nums w-16 text-right',
            isHealthy
              ? 'text-emerald-600 dark:text-emerald-400'
              : 'text-amber-600 dark:text-amber-400',
          )}
        >
          {ms.toFixed(1)} ms
        </span>
      </div>
    </div>
  );
}

export function SystemHealthWidget() {
  const { data, isLoading, error, refetch } = useStatusCheck();

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="pb-3 flex-shrink-0">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-sm">
            <span
              className={cn(
                'h-2 w-2 rounded-full flex-shrink-0',
                data ? 'bg-[#00D4A4] animate-pulse' : 'bg-muted',
              )}
            />
            System status
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => refetch()}
            disabled={isLoading}
            className="h-7 gap-1.5 text-xs"
          >
            <RefreshCw className={cn('h-3 w-3', isLoading && 'animate-spin')} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-0 flex-1 overflow-y-auto max-h-[380px] space-y-3">
        {error && (
          <Alert variant="destructive">
            <AlertDescription className="text-xs">
              Failed to reach API: {error.message}
            </AlertDescription>
          </Alert>
        )}
        {isLoading && !data && (
          <div className="space-y-3">
            {[1, 2].map((i) => (
              <div key={i} className="h-4 animate-pulse rounded bg-muted" />
            ))}
          </div>
        )}
        {data?.stats && (
          <div className="space-y-3">
            {data.stats.database && (
              <LatencyBar
                label="Database"
                icon={Database}
                latencyUs={data.stats.database.latencyUs}
                status={data.stats.database.status}
              />
            )}
            {data.stats.temporal && (
              <LatencyBar
                label="Temporal"
                icon={Server}
                latencyUs={data.stats.temporal.latencyUs}
                status={data.stats.temporal.status}
              />
            )}
          </div>
        )}
        {!isLoading && !error && !data && (
          <p className="text-xs text-muted-foreground">Click Refresh to check.</p>
        )}
      </CardContent>
    </Card>
  );
}
